/* eslint no-param-reassign: 0 */

import {
  DirectoryGroupingConsts,
  ItemTypeAPIs,
  ItemTypeConsts,
  ItemTypeDisplayNames,
} from 'client/util/Constants';
import { parallelLimit } from 'async';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ProgressMixin from 'client/store/mixins/ProgressMixin';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import _ from 'lodash';
import pluralize from 'pluralize';

// Small helper to send the correct type of association
export function targetTypeTranslation(type, targetId, items) {
  const {
    application,
    applicationV2,
    directory,
  } = ItemTypeConsts;
  let targetType = '';
  switch (type) {
    case directory:
      targetType = _.find(items, item => item.id === targetId).type;
      break;

    case applicationV2:
      targetType = application;
      break;

    default:
      targetType = type;
      break;
  }

  return targetType;
}

/**
 * The DirectoryGroupingModule is a Vuex module that allows clients to manage
 * grouping operations. This is a port of the legacy directoryGroupingUtils.js
 * to Vuex. It exposes actions so that callers can add, remove and update
 * associations. The actions handle batch requests so that complexity doesn't
 * need to be pushed to the DirectoryGroupingApi. Getters are also exposed so
 * that the status of batch requests can be surfaced to the admin user in a
 * progress modal.
 */
export default {
  namespaced: true,
  state: {},
  actions: {
    ...ProgressMixin.actions,
    /**
     * Bind a target assocation with a parent object. e.g. user to system, user
     * to user group, etc.
     * @param payload An object with the following properties:
     * @param payload.parentType The type of the parent object, e.g. system.
     * @param payload.parentId The ID of the parent object
     * @param payload.targetType The type of the target, or child, object, e.g.
     *  user.
     * @param payload.selectedTargetIds An array of target object IDs to bind
     *  to the parent object.
     * @param payload.unselectedTargetIds An array of target object IDs to
     *  unbind from the parent object.
     * @param payload.selectedItems An array of the items to bind to the parent
     *  object. Used to get the type of directory when binding one to a parent.
     * @param payload.unselectedItems An array of the items to unbind from the
     *  parent object. Used to get the type of directory when unbinding one from
     *  a parent.
     * @returns A promise that will be resolved when all requests are finished.
     * @todo Currently, errors are _not_ returned to the caller. This mimics
     * the current behavior in directoryGroupingUtils.js. Rather than abort
     * processing, batch processing continues. Eventually, errors will need to
     * be surfaced to the caller and displayed in the UI!
     */
    bindAssociations({
      dispatch,
    }, {
      parentType,
      parentId,
      targetType,
      selectedTargetIds = [],
      unselectedTargetIds = [],
      selectedItems = [],
      unselectedItems = [],
    }) {
      return new Promise((resolve) => {
        const { add, remove } = DirectoryGroupingConsts.operations;

        const tasks = [];
        const response = { numSuccess: 0, failedTasks: [] };

        selectedTargetIds.forEach((targetId) => {
          tasks.push((cb) => {
            DirectoryGroupingApi
              .bindAssociation({
                parentType,
                parentId,
                operation: add,
                targetType: targetTypeTranslation(targetType, targetId, selectedItems),
                targetId,
              })
              .then(() => {
                response.numSuccess += 1;
                dispatch('incrementCompleted');
                cb();
              })
              .catch((error) => {
                response.failedTasks.push({ id: targetId, error });
                dispatch('incrementCompleted');
                cb();
              });
          });
        });

        unselectedTargetIds.forEach((targetId) => {
          tasks.push((cb) => {
            DirectoryGroupingApi
              .bindAssociation({
                parentType,
                parentId,
                operation: remove,
                targetType: targetTypeTranslation(targetType, targetId, unselectedItems),
                targetId,
              })
              .then(() => {
                response.numSuccess += 1;
                dispatch('incrementCompleted');
                cb();
              })
              .catch((error) => {
                response.failedTasks.push({ id: targetId, error });
                dispatch('incrementCompleted');
                cb();
              });
          });
        });

        if (tasks.length) {
          dispatch('updateBulkActionStatus', true);

          dispatch('setCounter', {
            completed: 0,
            total: tasks.length,
          });

          // TODO: Should callers be informed of errors? What if only one request
          // failed?
          parallelLimit(tasks, 5, () => {
            dispatch('setCounter', {
              completed: tasks.length,
              total: tasks.length,
            });

            dispatch('updateBulkActionStatus', false);
            resolve(response);
          });
        } else {
          resolve();
        }
      });
    },

    /**
     * Update the attributes of a target directly bound to a parent object, e.g.
     * change the sudo status of a user bound to a system.
     * @param payload An object with the following properties:
     * @param payload.parentType The type of the parent object, e.g. system.
     * @param payload.parentId The ID of the parent object
     * @param payload.targetType The type of the target, or child, object, e.g.
     *  user.
     * @param payload.targetsToUpdate A hash of target IDs to attributes to
     * update, e.g.:
     * { '000': { 'sudo': { enabled: true } }, '001': { 'sudo': { enabled: false } } }
     * @returns A promise that will be resolved when all requests are finished.
     * @todo Currently, errors are _not_ returned to the caller. This mimics
     * the current behavior in directoryGroupingUtils.js. Rather than abort
     * processing, batch processing continues. Eventually, errors will need to
     * be surfaced to the caller and displayed in the UI!
     */
    bindAttributes({
      dispatch,
    }, {
      parentType,
      parentId,
      targetType,
      targetsToUpdate,
    }) {
      return new Promise((resolve) => {
        const { update } = DirectoryGroupingConsts.operations;
        const tasks = [];
        const response = { numSuccess: 0, failedTasks: [] };

        Object.keys(targetsToUpdate).forEach((targetId) => {
          tasks.push((cb) => {
            DirectoryGroupingApi
              .bindAssociation({
                parentType,
                parentId,
                operation: update,
                targetType,
                targetId,
                targetAttributes: targetsToUpdate[targetId],
              })
              .then(() => {
                response.numSuccess += 1;
                dispatch('incrementCompleted');
                cb();
              })
              .catch((error) => {
                response.failedTasks.push({ id: targetId, error });
                dispatch('incrementCompleted');
                cb();
              });
          });
        });

        if (tasks.length) {
          dispatch('updateBulkActionStatus', true);

          dispatch('setCounter', {
            completed: 0,
            total: tasks.length,
          });

          // TODO: Should callers be informed of errors? What if only one request
          // failed?
          parallelLimit(tasks, 5, () => {
            dispatch('setCounter', {
              completed: tasks.length,
              total: tasks.length,
            });

            dispatch('updateBulkActionStatus', false);
            resolve(response);
          });
        } else {
          resolve();
        }
      });
    },

    /**
     * If there are unselected target IDs then this method will first prompt
     * the admin asking them if they'd like to continue. If the admin chooses
     * to continue then the bindAssociations action will be dispatched. If the
     * admin chooses not to continue then the promise returned from this action
     * will be resolved.
     *
     * A new promise is returned here because control is passed to the modal
     * dialog and the caller needs to know when this method is complete because
     * it is called in series from an aside panel when binding/unbinding
     * multiple resources.
     */
    confirmAndBindAssociations({ dispatch }, {
      parentType,
      parentId,
      targetType,
      selectedTargetIds,
      unselectedTargetIds,
      selectedItems,
      unselectedItems,
    }) {
      return new Promise((resolve) => {
        const { deviceGroup, systemGroup } = DirectoryGroupingConsts.groupTypes;

        const payload = {
          parentType,
          parentId,
          targetType,
          selectedTargetIds,
          unselectedTargetIds,
          selectedItems,
          unselectedItems,
        };

        if (unselectedTargetIds.length) {
          const parentTypeName = ItemTypeDisplayNames[parentType];

          // HY-1602: needed to change User Group -> Device Group unbind modal "Are you sure?" text
          // without changing the field in payload
          const itemTypeName = targetType === systemGroup
            ? ItemTypeDisplayNames[deviceGroup]
            : ItemTypeDisplayNames[targetType];

          const targetTypeName = pluralize(itemTypeName, unselectedTargetIds.length);
          const pronoun = pluralize('this', unselectedTargetIds.length);

          const componentProps = {
            headerText: 'Are you sure?',
            mdTemplate: `You are unbinding this ${parentTypeName} from **${unselectedTargetIds.length}** ${targetTypeName}.\n\nAll members of this ${parentTypeName} will lose access to ${pronoun} ${targetTypeName}. Are you sure you would like to continue?`,
            continueText: 'yes',
            handleCancel: () => {
              ModalEventBus.close();
              resolve(false); // user cancelled, return false to stay in Aside
            },
            handleContinue: () => {
              ModalEventBus.close();
              dispatch('bindAssociations', payload).then(() => resolve());
            },
            mainActionAttention: true,
          };

          ModalEventBus.open({
            isLargeModal: false,
            component: 'ConfirmationModal',
            componentProps,
          });
        } else {
          dispatch('bindAssociations', payload).then(() => resolve());
        }
      });
    },

    /**
     * Add the member to or remove the member from the specified group.
     */
    updateMember(context, {
      groupId,
      groupType,
      operation,
      memberId,
      memberType,
    }) {
      return DirectoryGroupingApi
        .bindAssociation({
          parentType: groupType,
          parentId: groupId,
          operation,
          targetType: memberType,
          targetId: memberId,
        });
    },

    async fetchBoundDirectories(context, { parentType, parentId }) {
      const associationUrl = `/api/v2/${parentType}/${parentId}/${ItemTypeAPIs[ItemTypeConsts.directory]}`;
      const boundItems = await DirectoryGroupingApi.getAllItems(associationUrl) || [];
      if (!boundItems.length) return [];
      const directories = await DirectoryApi.fetchAll();
      return directories.filter(directory => !!boundItems.find(({ id }) => id === directory.id));
    },

    async fetchAssociationDetails(context, { parentType, parentId, childType }) {
      const apiMapping = {
        [ItemTypeAPIs[ItemTypeConsts.application]]: ApplicationApi,
        [ItemTypeAPIs[ItemTypeConsts.radiusServer]]: RadiusServerApi,
      };

      const childApi = apiMapping[childType];

      if (!childApi) {
        throw new Error(`No api registered for ${childType}`);
      }
      const associationUrl = `/api/v2/${parentType}/${parentId}/${childType}`;
      const boundItems = await DirectoryGroupingApi.getAllItems(associationUrl);

      if (!boundItems.length) return [];

      const boundItemDetailsRequests = boundItems.map(({ id }) => childApi.get(id));
      const boundItemDetails = await Promise.all(boundItemDetailsRequests);

      return boundItemDetails;
    },
  },

  getters: {},

  mutations: {},
};
